/**
 * useSetSpecialtySets — PUT /v1/admin/clinics/{clinic_id}/specialty-sets
 *
 * Replace-all the clinic's declared specialty sets. New in backend v0.4.0.
 *
 * Empty array clears the set. Non-optimistic per the established mutation
 * pattern — invalidate dashboard + admin-clinics on success and let the
 * server be the source of truth.
 *
 * Dashboard invalidation is critical: changing the specialty set changes
 * the clinic's EMA-aware `quality.max`, which changes the entire score-
 * card row's weighted contribution. The AM should see the updated number
 * immediately after save.
 *
 * Note: the controlled-dropdown UX for picking valid codes depends on a
 * separate backend endpoint (GET /v1/admin/specialty-sets) that doesn't
 * exist in v0.4.0. Until that lands, the modal field is disabled — see
 * MIPS-Dashboard-Frontend-v1.2.0-claude-session-handoff.md.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { getApiClient } from "@/api/client";
import type {
  ClinicSpecialtySetRequest,
  ClinicSpecialtySetResponse,
} from "@/api/models";
import { queryKeys } from "./queryKeys";

export interface SetSpecialtySetsVariables {
  clinicId: number;
  body: ClinicSpecialtySetRequest;
}

export function useSetSpecialtySets() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      clinicId,
      body,
    }: SetSpecialtySetsVariables): Promise<ClinicSpecialtySetResponse> => {
      const { data } = await getApiClient().put<ClinicSpecialtySetResponse>(
        `/v1/admin/clinics/${clinicId}/specialty-sets`,
        body,
      );
      return data;
    },
    onSuccess: (_data, vars) => {
      // The dashboard's quality.max depends on this — invalidate the full
      // dashboard tree for the clinic.
      void qc.invalidateQueries({
        queryKey: queryKeys.dashboard.byClinic(vars.clinicId),
      });
      // Admin clinics list shows specialty_set_codes inline — refresh.
      void qc.invalidateQueries({ queryKey: queryKeys.adminClinics.all });
    },
  });
}
